"use client"

import type { ReactNode } from "react"
import { Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { Edit, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { TabHeader, TabSurface } from "@/components/graphite/surface"
import type { Placement } from "openapi/api/model"

type PlacementWithCandidate = Placement & { candidate_id?: string | null }

interface PlacementSummaryListProps {
  placements: PlacementWithCandidate[]
  emptyTitle: string
  emptyDescription: string
  showCandidate?: boolean
  action?: ReactNode
  signatureCompanyId?: string | null
  useJobCompanySignature?: boolean
  onEditPlacement?: (placementId: string) => void
  onDeletePlacement?: (placementId: string) => void
  deletingPlacementId?: string | null
}

const FIGURE_LABEL = "text-[11px] font-medium text-muted-foreground"

function formatPlacementDate(value?: string | null) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return format(date, "d MMM yyyy")
}

function formatMoney(value?: number | null) {
  if (value == null) return "—"
  return `$${value.toLocaleString()}`
}

/**
 * Placements, on the Graphite frame.
 *
 * Money, percentages and dates are machine values, so they are mono; labels are
 * language, so they are Inter. Rows are hairline-separated inside one surface
 * rather than a stack of cards — the canvas rule is that a surface holds
 * content, it does not nest more surfaces inside itself.
 */
export function PlacementSummaryList({
  placements,
  emptyTitle,
  emptyDescription,
  showCandidate = false,
  action,
  signatureCompanyId,
  useJobCompanySignature = false,
  onEditPlacement,
  onDeletePlacement,
  deletingPlacementId,
}: PlacementSummaryListProps) {
  if (!placements.length) {
    return (
      <TabSurface>
        <TabHeader label={emptyTitle} actions={action} />
        <p className="pt-4 text-[12.5px] text-muted-foreground">
          {emptyDescription}
        </p>
      </TabSurface>
    )
  }

  return (
    <TabSurface>
      <TabHeader label="Placements" count={placements.length} actions={action} />

      <div className="pt-1">
        {placements.map((placement) => {
          const candidateId = placement.candidate_id ?? null
          const placementId = (placement as unknown as { placement_id: string })
            .placement_id

          return (
            <div key={placement.id} className="border-b py-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h4 className="break-words text-[13px] font-medium leading-snug">
                    {placement.job_title ?? "Placement"}
                    {useJobCompanySignature ? (
                      <>
                        <span className="mx-1 text-muted-foreground">@</span>
                        {signatureCompanyId ? (
                          <Link
                            to="/community/companies"
                            search={{ companyId: signatureCompanyId }}
                            className="text-brand-ink transition-opacity hover:opacity-80"
                          >
                            {placement.company_name ?? "Unknown company"}
                          </Link>
                        ) : (
                          <span>{placement.company_name ?? "Unknown company"}</span>
                        )}
                      </>
                    ) : null}
                  </h4>
                  {!useJobCompanySignature && (
                    <p className="mt-[3px] break-words text-[11.5px] text-muted-foreground">
                      {placement.company_name ?? "Unknown company"}
                    </p>
                  )}
                  {showCandidate && (
                    <p className="mt-1.5 text-[11.5px]">
                      {candidateId ? (
                        <Link
                          to="/candidates"
                          search={{ candidateId }}
                          className="text-brand-ink transition-opacity hover:opacity-80"
                        >
                          {placement.candidate_name ?? "Unknown candidate"}
                        </Link>
                      ) : (
                        <span>{placement.candidate_name ?? "Unknown candidate"}</span>
                      )}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <span className="inline-flex items-center rounded-full border px-2.5 py-[3px] text-[11px] capitalize text-muted-foreground">
                    {placement.placement_type}
                  </span>
                  {onEditPlacement ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground"
                      onClick={() => onEditPlacement(placementId)}
                    >
                      <Edit className="h-3.5 w-3.5" />
                      <span className="sr-only">Edit placement</span>
                    </Button>
                  ) : null}
                  {onDeletePlacement ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 w-7 p-0 text-muted-foreground",
                        "hover:bg-destructive/10 hover:text-destructive",
                      )}
                      onClick={() => onDeletePlacement(placementId)}
                      disabled={deletingPlacementId === placementId}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="sr-only">Delete placement</span>
                    </Button>
                  ) : null}
                </div>
              </div>

              {/* Three figures on one hairline row — no boxes inside the surface. */}
              <div className="mt-3 grid grid-cols-3 gap-4">
                <div>
                  <div className="font-mono text-[13px] font-medium text-success">
                    {formatMoney(placement.salary)}
                  </div>
                  <div className={cn(FIGURE_LABEL, "mt-[3px]")}>Salary</div>
                </div>
                <div>
                  <div className="font-mono text-[13px] font-medium">
                    {placement.fee_type === "percentage"
                      ? `${placement.fee_amount}%`
                      : formatMoney(placement.fee_amount)}
                  </div>
                  <div className={cn(FIGURE_LABEL, "mt-[3px]")}>Fee</div>
                </div>
                <div>
                  <div className="font-mono text-[13px] font-medium">
                    {formatPlacementDate(placement.start_date)}
                  </div>
                  <div className={cn(FIGURE_LABEL, "mt-[3px]")}>Start date</div>
                </div>
              </div>

              {placement.splits?.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {placement.splits.map((split) => (
                    <span
                      key={split.id ?? `${placement.id}-${split.owner_name}`}
                      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11px] text-muted-foreground"
                    >
                      {split.owner_name}
                      <span className="font-mono text-foreground">
                        {split.percentage}%
                      </span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </TabSurface>
  )
}
